<template>
  <div class="carousel-wrapper">
    <div
      v-if="carouselTitle || carouselDescription || $slots.default"
      class="carousel-intro"
    >
      <h2 v-if="carouselTitle">
        {{ carouselTitle }}
      </h2>
      <p v-if="carouselDescription">
        {{ carouselDescription }}
      </p>

      <slot />
    </div>
    <vue-glide
      v-if="carouselSlides.length"
      ref="slider"
      :type="carouselType"
      :start-at="carouselStartAt"
      :bound="carouselBound"
      :rewind="carouselRewind"
      :focus-at="carouselFocusAt"
      :per-view="carouselPerView"
      :gap="carouselGap"
      :bullet="carouselShowBullets"
      :breakpoints="carouselBreakpoints"
      :peek="carouselPeek"
      class="carousel"
      :class="{ 'no-bullets': totalSlides < 3, '-ml-5': !noIndent }"
      @glide:move-after="carouselArrowStates"
    >
      <vue-glide-slide
        v-for="(slide, i) in carouselSlides"
        :key="i"
        class="carousel-slide"
      >
        <div class="carousel-card">
          <div class="carousel-card-content">
            <div
              v-if="slide.imageUrl"
              class="carousel-card-content-img-container"
              :class="{ 'has-no-curve': !slideHasContent(slide)}"
            >
              <img
                class="carousel-card-content-img"
                :src="slide.imageUrl"
                alt=""
              >
            </div>
            <div
              v-if="slideHasContent(slide)"
              class="carousel-card-content-container"
            >
              <span
                v-if="slide.caption"
                class="carousel-card-content-caption"
              >{{ slide.caption }}</span>
              <p
                v-if="slide.description"
                class="carousel-card-content-text"
              >
                {{ slide.description }}
              </p>
            </div>
            <a
              v-if="slide.url"
              class="carousel-card-link"
              :href="slide.url"
              :target="hasExternalLink(slide.url) ? '_blank' : null "
              :rel="hasExternalLink(slide.url) ? 'noopener noreferrer' : null"
              aria-label="link to article"
            />
          </div>
        </div>
      </vue-glide-slide>
      <template
        v-if="carouselShowArrows"
        slot="control"
      >
        <button
          data-glide-dir="<"
          class="glide__arrow glide__arrow--left"
          :class="{ 'glide__arrow--disabled': noPrevSlide && !carouselRewind }"
        >
          <chevron-left class="icon" />
        </button>
        <button
          data-glide-dir=">"
          class="glide__arrow glide__arrow--right"
          :class="{ 'glide__arrow--disabled': noNextSlide && !carouselRewind }"
        >
          <chevron-right class="icon" />
        </button>
      </template>
    </vue-glide>
  </div>
</template>

<script>
import { Glide, GlideSlide } from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';

export default {
  name: 'KfCarousel',
  components: {
    ChevronLeft: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ChevronLeft.vue'),
    ChevronRight: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ChevronRight.vue'),
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
  },
  props: {
    carouselTitle: {
      type: String,
      default: '',
    },
    carouselDescription: {
      type: String,
      default: '',
    },
    carouselType: {
      type: String,
      default: 'slider',
    },
    carouselSlides: {
      type: Array,
      default: null,
    },
    carouselStartAt: {
      type: Number,
      default: 0,
    },
    carouselBound: {
      type: Boolean,
      default: false,
    },
    carouselRewind: {
      type: Boolean,
      default: false,
    },
    carouselFocusAt: {
      type: [String, Number],
      default: 0,
    },
    carouselPerView: {
      type: Number,
      default: 3,
    },
    carouselGap: {
      type: Number,
      default: 25,
    },
    carouselShowArrows: {
      type: Boolean,
      default: false,
    },
    carouselShowBullets: {
      type: Boolean,
      default: false,
    },
    carouselBreakpoints: {
      type: Object,
      default: () => ({}),
    },
    carouselPeek: {
      type: Object,
      default: () => ({ before: 0, after: 0 }),
    },
    noIndent: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      noPrevSlide: false,
      noNextSlide: false,
    };
  },
  computed: {
    totalSlides() {
      return this.carouselSlides.length;
    },
  },
  methods: {
    carouselArrowStates() {
      if (this.$refs.slider.glide.index === 0) {
        this.noPrevSlide = true;
      } else {
        this.noPrevSlide = false;
      }

      if (this.$refs.slider.glide.index + 1 === this.totalSlides) {
        this.noNextSlide = true;
      } else {
        this.noNextSlide = false;
      }
    },
    slideHasContent(slide) {
      return (slide?.caption && slide?.caption !== '') || (slide?.description && slide?.description !== '');
    },
    hasExternalLink(url) {
      const pattern = /^((http|https):\/\/)/;
      let isExernal = false;

      if(pattern.test(url)) {
        isExernal = true
      }

      return isExernal;
    },
  },
};
</script>

<style lang="postcss">
.carousel {
  @apply relative;

  &-intro {
    @apply mb-8;

    @screen md {
      @apply mb-12;
    }

    *:last-child {
      @apply mb-0;
    }
  }

  &-wrapper {
    @apply relative;
  }

  .glide__track {
    @apply pl-5;
  }

  .glide__slides {
    @apply list-none pb-5;

    .glide__slide {
      @apply relative h-auto;

      .carousel-image {
        @apply w-full rounded;
      }
    }
  }

  &-card {
    @apply flex flex-col self-center
  }

  &-card-content {
    @apply bg-white shadow-md rounded overflow-hidden h-full;

    &-img {

    }

    &-container {
      @apply pt-4 pr-6 pb-4 pl-6;
    }

    &-caption {
      @apply block text-sm text-gray-300 mb-2;
    }

    &-link {
      @apply absolute top-0 right-0 bottom-0 left-0;
    }
  }

  // arrows container
  div[data-glide-el=controls] {
    @apply absolute flex top-1/2 transform h-9 w-full;
    transform: translateY(-50%);

    .glide__arrow {
      @apply flex justify-center items-center absolute bg-white text-black p-2 opacity-100 cursor-pointer shadow-md rounded-full;
      z-index: 2;
      will-change: opacity;
      transition: opacity 150ms ease, border 300ms ease-in-out;
      height: 36px;
      width: 36px;

      .icon {
        @apply text-black;
        width: 13px;
        height: 13px;

        path {
          stroke: var(--color-primary-alpha);
        }
      }

      &:hover,
      &:focus {
        @apply bg-secondary-alpha;

        .icon {
          @apply text-black;
        }
      }



      &:focus {
        @apply outline-none;
      }

      &--left {
        left: 2px;
      }

      &--right {
        right: -18px;
      }

      &--disabled {
        opacity: 0.33;
        @apply pointer-events-none;
      }
    }

  }

  // Bullets
  .glide__bullets {
    @apply absolute bottom-0;

    .glide__bullet {
      @apply bg-primary-bravo w-3 h-3 p-0 rounded border-b-2 border-transparent cursor-pointer mx-1 my-0;
      transition: all 300ms ease-in-out;
      line-height: 0;
      box-shadow: 0 0.25em 0.5em 0 rgba(0, 0, 0, 0.1);

      &:focus {
        @apply outline-none;
      }

      &:hover,
      &:focus {
        @apply border-secondary-alpha border-2 border-solid;
      }

      &--active {
        @apply bg-secondary-alpha;
      }
    }
  }
}
</style>
